/*
 * Small presentational pieces used by both halves of the site.
 *
 * Kept in one file because each is a handful of lines and they are always imported
 * together; a directory of eight one-component files would be more filing than
 * code.
 *
 * Their own words - "Loading…", "Try again", "Previous", the private tag's - are
 * the chosen language's since S48 (2026-10-29), and so are the dates DateTime
 * writes; the status badges' too since the admin site followed the same day.
 */

import { useEffect, useId, useRef, type ReactNode } from 'react';
import type { ArticlePublishingStatus, ArticleStatus } from '@accotool-kb/shared';
import { ApiError } from '../api/client.js';
import { useLanguage, useMessages } from '../i18n/language.js';

export function Spinner({ label }: { label?: string }): JSX.Element {
  const t = useMessages();
  return (
    <div className="spinner" role="status" aria-live="polite">
      <span className="spinner-dot" />
      <span className="visually-hidden">{label ?? t.common.loadingLabel}</span>
    </div>
  );
}

export function Loading({ label }: { label?: string }): JSX.Element {
  const t = useMessages();
  return (
    <div className="loading-block">
      <Spinner {...(label ? { label } : {})} />
      <p>{label ?? t.common.loading}</p>
    </div>
  );
}

/**
 * Renders whatever was thrown. ApiError carries a message written for a user, so it
 * is shown as-is; anything else gets a generic line, because an unexpected error's
 * message is usually a stack-trace fragment.
 *
 * As-is in English, that is. In Danish the message goes through the dictionary's
 * errors.describe (S48), which knows the server's fixed messages a reader can
 * meet and shows anything else as it came.
 */
export function ErrorBanner({
  error,
  onRetry,
  onDismiss,
}: {
  error: unknown;
  onRetry?: () => void;
  onDismiss?: () => void;
}): JSX.Element | null {
  const t = useMessages();
  if (!error) return null;

  const apiError = error instanceof ApiError ? error : null;
  const message = t.errors.describe(
    apiError?.code ?? null,
    apiError ? apiError.message : error instanceof Error ? error.message : t.common.somethingWentWrong,
  );

  const fieldErrors = apiError ? Object.entries(apiError.details) : [];

  return (
    <div className="notice notice-error" role="alert">
      <div className="notice-body">
        <strong>{message}</strong>
        {fieldErrors.length > 0 && (
          <ul>
            {fieldErrors.map(([field, messages]) => (
              <li key={field}>
                <code>{field}</code>: {messages.join(' ')}
              </li>
            ))}
          </ul>
        )}
        {apiError?.isConcurrencyConflict && (
          <p>{t.common.concurrencyConflict}</p>
        )}
      </div>
      <div className="notice-actions">
        {onRetry && (
          <button type="button" className="button button-quiet" onClick={onRetry}>
            {t.common.tryAgain}
          </button>
        )}
        {onDismiss && (
          <button type="button" className="button button-quiet" onClick={onDismiss}>
            {t.common.dismiss}
          </button>
        )}
      </div>
    </div>
  );
}

export function EmptyState({
  title,
  children,
}: {
  title: string;
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className="empty-state">
      <h2>{title}</h2>
      {children}
    </div>
  );
}

export function Tag({
  children,
  kind = 'neutral',
  title,
}: {
  children: ReactNode;
  kind?: 'neutral' | 'product' | 'category' | 'audience';
  title?: string;
}): JSX.Element {
  return (
    <span className={`tag tag-${kind}`} {...(title ? { title } : {})}>
      {children}
    </span>
  );
}

/**
 * Who a private article is for (2026-10-26, S46): one small tag per audience, as
 * the owner asked, beside the product and category tags in the article view and
 * in the article list. Nothing for a public article. A private article whose
 * every audience has since been unconfigured - only an administrator reads that
 * one - still says it is private, rather than looking public.
 *
 * The padlock is decoration; the words carry it for a screen reader, which hears
 * "Private, for accoTOOL Partners".
 */
export function AudienceTags({
  article,
}: {
  article: { isPrivate: boolean; audiences: string[] };
}): JSX.Element | null {
  const t = useMessages();
  if (!article.isPrivate) return null;
  const names = article.audiences.length > 0 ? article.audiences : [null];
  return (
    <>
      {names.map((name) => (
        <Tag
          key={`a-${name ?? ''}`}
          kind="audience"
          title={name ? t.audience.privateFor(name) : t.audience.privateForNobody}
        >
          <svg className="tag-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path d="M5 7V5a3 3 0 0 1 6 0v2h.5A1.5 1.5 0 0 1 13 8.5v5a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 3 13.5v-5A1.5 1.5 0 0 1 4.5 7H5Zm1.5 0h3V5a1.5 1.5 0 0 0-3 0v2Z" />
          </svg>
          <span className="visually-hidden">{name ? t.audience.hiddenPrivateFor : t.audience.hiddenPrivate}</span>
          {name}
        </Tag>
      ))}
    </>
  );
}

/*
 * Two badges, because there are two different questions and one answer would be a
 * lie in half the cases:
 *
 *   PublishingStatusBadge  can a reader see this? All three gates, so it can say
 *                          'unwritten' - which is not an editorial decision at all.
 *                          This is what every list row and article header shows.
 *   ArticleStatusBadge     what did the author ask for? The two editorial gates
 *                          only, so an article set to publish reads as 'published'
 *                          even with nothing written in it. Only the publishing
 *                          controls have any use for this.
 *
 * Both carry their explanation as a tooltip, so the colour is never the only signal.
 *
 * PublishingStatusBadge also labels a release note (S42, 2026-10-12): same rule,
 * same states, so only the noun in the tooltip differs - 'article' by default,
 * which keeps every article tooltip as it was.
 */
export function PublishingStatusBadge({
  status,
  noun = 'article',
}: {
  status: ArticlePublishingStatus;
  /** What the tooltip calls the thing, mid-sentence: 'article' or 'release note'. */
  noun?: string;
}): JSX.Element {
  const { label, detail } = useMessages().admin.rules.publishingStatus(status, noun);
  return (
    <span className={`badge badge-${status}`} title={detail}>
      {label}
    </span>
  );
}

export function ArticleStatusBadge({ status }: { status: ArticleStatus }): JSX.Element {
  const { label, detail } = useMessages().admin.rules.articleStatus(status);
  return (
    <span className={`badge badge-${status}`} title={detail}>
      {label}
    </span>
  );
}

export function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
  required,
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  error?: string | undefined;
  children: ReactNode;
  required?: boolean;
}): JSX.Element {
  return (
    <div className={`field${error ? ' field-invalid' : ''}`}>
      <label htmlFor={htmlFor}>
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </label>
      {children}
      {hint && !error && <p className="field-hint">{hint}</p>}
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * A question-mark button that reveals an explanation.
 *
 * WHY A BUTTON AND NOT A `title` ATTRIBUTE. Most tooltips in this app are native
 * `title`s on controls that already say what they do, where the tooltip only adds
 * a detail. This one carries the whole explanation of a field, so it has to be
 * reachable without a mouse - a native `title` never appears on keyboard focus or
 * on a tap - and it has to be able to hold more than one sentence, which a
 * `title` renders as an unstyled run-on line.
 *
 * NO JAVASCRIPT, DELIBERATELY. The bubble is revealed by `:hover` and
 * `:focus-within` on the wrapper, which covers all three input methods without a
 * line of state: a pointer hovers, a keyboard tabs to the button, and a tap
 * focuses it. Blurring or moving away hides it again. Click-to-toggle would need
 * state, an `aria-expanded`, and a decision about outside clicks, for no
 * behaviour this does not already have.
 *
 * The bubble stays in the DOM while hidden. It is referenced by the trigger's
 * `aria-describedby`, and a node referenced that way is used for the description
 * even when it is hidden - so the explanation reaches a screen reader without the
 * text also being loose on the page for a virtual cursor to trip over.
 */
export function HelpTooltip({
  label,
  children,
}: {
  /**
   * The button's accessible name - what a screen reader announces before the
   * description. Short: "How X works", not the explanation itself.
   */
  label: string;
  children: ReactNode;
}): JSX.Element {
  const id = useId();

  return (
    <span className="help-tip">
      <button type="button" className="help-tip-trigger" aria-label={label} aria-describedby={id}>
        {/* aria-hidden: the accessible name is on the button, as with the toolbar icons. */}
        <svg
          width={15}
          height={15}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden={true}
          focusable={false}
        >
          <circle cx="8" cy="8" r="6.5" />
          <path d="M6.05 6.05a2 2 0 0 1 3.9.65c0 1.35-1.95 2-1.95 2" />
          <path d="M8 11.35h.01" />
        </svg>
      </button>
      <span role="tooltip" id={id} className="help-tip-bubble">
        {children}
      </span>
    </span>
  );
}

/**
 * A modal dialog.
 *
 * Uses the native <dialog> element, which brings focus trapping, Escape handling
 * and the top layer without any of it having to be reimplemented. `closedby` is not
 * relied on: onCancel is intercepted so a destructive dialog cannot be dismissed by
 * a stray Escape while a delete is in flight.
 */
export function Modal({
  title,
  open,
  onClose,
  children,
  footer,
  wide,
  busy,
}: {
  title: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  busy?: boolean;
}): JSX.Element | null {
  const t = useMessages();
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;

    if (open && !element.open) {
      /*
       * Focus, which took two attempts to get right - the first was based on a
       * false premise and the tests agreed with it.
       *
       * A closed <dialog> is display:none, and .focus() on a display:none
       * element does nothing. React applies `autoFocus` imperatively during
       * commit - BEFORE this effect calls showModal - so on a dialog it is
       * always a no-op, and it leaves no `autofocus` attribute behind either.
       * showModal() then honours a real `autofocus` attribute if it finds one
       * and otherwise focuses the first focusable descendant, which here is
       * always the header's close button. Measured in a browser, all of it.
       *
       * So the target is named explicitly with `data-autofocus` and focused
       * after showModal, when it is finally focusable. A dialog that names no
       * target keeps the browser's default.
       */
      element.showModal();
      const target = element.querySelector<HTMLElement>('[data-autofocus]');
      if (target) target.focus();
    }
    if (!open && element.open) element.close();
  }, [open]);

  if (!open) return null;

  return (
    <dialog
      ref={dialog}
      className={`modal${wide ? ' modal-wide' : ''}`}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
      aria-labelledby="modal-title"
    >
      <header className="modal-header">
        <h2 id="modal-title">{title}</h2>
        <button
          type="button"
          className="button button-icon"
          onClick={onClose}
          disabled={busy}
          aria-label={t.common.close}
        >
          ×
        </button>
      </header>
      <div className="modal-body">{children}</div>
      {footer && <footer className="modal-footer">{footer}</footer>}
    </dialog>
  );
}

export function Pagination({
  page,
  pageSize,
  total,
  onChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
}): JSX.Element | null {
  const t = useMessages();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(total, page * pageSize);

  return (
    <nav className="pagination" aria-label={t.pagination.label}>
      <button
        type="button"
        className="button button-quiet"
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
      >
        {t.pagination.previous}
      </button>
      <span aria-live="polite">{t.pagination.range(first, last, total)}</span>
      <button
        type="button"
        className="button button-quiet"
        onClick={() => onChange(page + 1)}
        disabled={page >= pages}
      >
        {t.pagination.next}
      </button>
    </nav>
  );
}

/**
 * Dates are stored and transported as UTC. Showing them in the reader's own
 * timezone is what they expect; the ISO value stays in the title attribute so a
 * scheduled instant can always be read exactly.
 *
 * Written in the chosen language's locale since S48 (2026-10-29) - "28 October
 * 2026", "28. oktober 2026" - where until then the browser's own locale decided,
 * which could put a Danish month name among English words or the other way round.
 */
export function DateTime({
  value,
  dateOnly,
}: {
  value: string;
  dateOnly?: boolean;
}): JSX.Element {
  const { language } = useLanguage();
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return <span>—</span>;

  const formatted = dateOnly
    ? date.toLocaleDateString(language.dateLocale, { year: 'numeric', month: 'long', day: 'numeric' })
    : date.toLocaleString(language.dateLocale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

  return (
    <time dateTime={value} title={`${value} (UTC)`}>
      {formatted}
    </time>
  );
}

/** A multi-select list of checkboxes, used for the product and category filters. */
export function CheckboxList<T extends { id: number; label: string; count?: number }>({
  items,
  selected,
  onChange,
  name,
}: {
  items: T[];
  selected: number[];
  onChange: (next: number[]) => void;
  name: string;
}): JSX.Element {
  const t = useMessages();
  const toggle = (id: number) => {
    onChange(selected.includes(id) ? selected.filter((value) => value !== id) : [...selected, id]);
  };

  if (items.length === 0) return <p className="muted">{t.common.nothingToFilterBy}</p>;

  return (
    <ul className="checkbox-list">
      {items.map((item) => (
        <li key={item.id}>
          <label>
            <input
              type="checkbox"
              name={name}
              checked={selected.includes(item.id)}
              onChange={() => toggle(item.id)}
            />
            <span>{item.label}</span>
            {item.count !== undefined && <span className="count">{item.count}</span>}
          </label>
        </li>
      ))}
    </ul>
  );
}
